import { useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export type AnalysisPhase = string;

interface UrlInputProps {
  onAnalyze: (url: string) => void;
  isAnalyzing: boolean;
  currentPhase?: string | null;
  queueInfo?: { position: number; estimatedWaitSeconds: number } | null;
}

const PHASE_MESSAGES: Record<string, string> = {
  queued: 'Waiting for an available browser slot...',
  rendering: 'Rendering your website...',
  ai_analysis: 'Scoring with Claude AI...',
  completed: 'Finalizing results...',
};

const DEFAULT_ANALYZING_MESSAGE = 'Launching browser...';

function getPhaseMessage(phase: string | null | undefined): string {
  if (!phase) return DEFAULT_ANALYZING_MESSAGE;
  if (phase.startsWith('queued:')) {
    const pos = parseInt(phase.slice('queued:'.length), 10);
    if (Number.isFinite(pos) && pos > 0) {
      return `You are #${pos} in the queue`;
    }
    return PHASE_MESSAGES.queued;
  }
  return PHASE_MESSAGES[phase] ?? DEFAULT_ANALYZING_MESSAGE;
}

const PROGRESS_STEPS: { key: 'rendering' | 'ai' | 'finalizing'; label: string }[] = [
  { key: 'rendering', label: 'Rendering' },
  { key: 'ai', label: 'AI Analysis' },
  { key: 'finalizing', label: 'Finalizing' },
];

function getActiveStepIndex(phase: string | null | undefined): number {
  switch (phase) {
    case 'queued':
    case 'rendering':
      return 0;
    case 'ai_analysis':
      return 1;
    case 'completed':
      return 2;
    default:
      return -1;
  }
}

function formatWait(seconds: number): string {
  if (seconds < 60) return `~${seconds}s wait`;
  const minutes = Math.round(seconds / 60);
  return `~${minutes}m wait`;
}

export function UrlInput({ onAnalyze, isAnalyzing, currentPhase, queueInfo }: UrlInputProps) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const { canAnalyze } = useAuth();

  const normalizeUrl = (raw: string): string => {
    const trimmed = raw.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    return `https://${trimmed}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    const { allowed, reason } = canAnalyze();
    if (!allowed) {
      setError(reason || 'Unable to analyze at this time');
      return;
    }

    try {
      const normalized = normalizeUrl(url);
      const urlObj = new URL(normalized);
      if (!urlObj.protocol.startsWith('http')) {
        throw new Error('Invalid protocol');
      }
      onAnalyze(normalized);
    } catch {
      setError('Please enter a valid domain (e.g., example.com or https://example.com)');
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-5xl font-normal text-black mb-4">Search With Apex Analyst</h1>
        <p className="text-xl text-gray-500">Analyze your website for AI search compatibility</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="text"
            id="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Enter your website URL (e.g. yourdomain.com)"
            disabled={isAnalyzing}
            className="w-full px-6 py-4 text-gray-600 border border-gray-300 rounded-full focus:outline-none focus:border-gray-400 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
          />
          {error && (
            <p className="mt-2 text-sm text-gray-600 text-center">{error}</p>
          )}
        </div>

        {isAnalyzing && (
          <div
            className="pt-4"
            role="progressbar"
            aria-label="Analysis progress"
            aria-valuemin={0}
            aria-valuemax={PROGRESS_STEPS.length}
            aria-valuenow={Math.max(0, Math.min(PROGRESS_STEPS.length, getActiveStepIndex(currentPhase)))}
          >
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              {PROGRESS_STEPS.map((step, idx) => {
                const activeIndex = getActiveStepIndex(currentPhase);
                const isComplete = activeIndex > idx;
                const isActive = activeIndex === idx;
                const isPending = activeIndex < idx;
                return (
                  <div key={step.key} className="flex items-center flex-1 last:flex-none">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors ${
                          isComplete
                            ? 'bg-black border-black text-white'
                            : isActive
                            ? 'bg-white border-black text-black'
                            : 'bg-white border-gray-300 text-gray-400'
                        }`}
                        aria-current={isActive ? 'step' : undefined}
                      >
                        {isComplete ? (
                          <Check className="w-4 h-4" />
                        ) : isActive ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <span className="text-xs font-medium">{idx + 1}</span>
                        )}
                      </div>
                      <span
                        className={`mt-2 text-xs font-medium text-center ${
                          isPending ? 'text-gray-400' : 'text-gray-900'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {idx < PROGRESS_STEPS.length - 1 && (
                      <div className="flex-1 h-0.5 mx-3 -mt-6 bg-gray-200 relative overflow-hidden">
                        <div
                          className={`absolute inset-y-0 left-0 bg-black transition-all duration-500 ${
                            isComplete ? 'w-full' : 'w-0'
                          }`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-center">
          <button
            type="submit"
            disabled={isAnalyzing}
            className="bg-black text-white font-medium py-3 px-12 rounded-full hover:bg-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-lg min-w-[200px]"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" />
                <span className="truncate">{getPhaseMessage(currentPhase)}</span>
              </>
            ) : (
              'Analyze'
            )}
          </button>
        </div>

        {isAnalyzing && currentPhase === 'queued' && queueInfo && (
          <p className="text-center text-sm text-gray-600">
            You are #{queueInfo.position} in the queue ({formatWait(queueInfo.estimatedWaitSeconds)})
          </p>
        )}

        {isAnalyzing && (
          <p className="text-center text-sm text-gray-400">
            This may take up to 30 seconds while we fully render your site
          </p>
        )}
      </form>
    </div>
  );
}
